import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AuthShell, AuthIcon } from '../../components/layout/AuthShell';
import { Eye, EyeOff, Key, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Kein gültiger Token vorhanden. Bitte fordere einen neuen Link an.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="reset-password-page"
      eyebrow="Passwort"
      title={<>Neues Passwort <span className="serif-it text-purple">vergeben.</span></>}
      intro="Gib dein neues Passwort ein."
      below={!success && token && (
        <Link to="/login" className="text-ink/60 hover:text-ink" data-testid="reset-password-back-login">
          ← Zurück zur Anmeldung
        </Link>
      )}
    >
      {success ? (
        <div className="text-center py-2" data-testid="reset-password-success">
          <AuthIcon tone="green"><CheckCircle2 className="w-8 h-8" /></AuthIcon>
          <h2 className="text-xl font-bold mb-2">Passwort geändert!</h2>
          <p className="text-ink/80 mb-6">
            Dein Passwort wurde erfolgreich geändert. Du wirst zur Anmeldung weitergeleitet...
          </p>
          <Button asChild className="btn--block">
            <Link to="/login">Jetzt anmelden</Link>
          </Button>
        </div>
      ) : !token ? (
        <div className="text-center py-2" data-testid="reset-password-invalid">
          <AuthIcon tone="red"><AlertCircle className="w-8 h-8" /></AuthIcon>
          <h2 className="text-xl font-bold mb-2">Ungültiger Link</h2>
          <p className="text-ink/80 mb-6">{error}</p>
          <Button asChild className="btn--block">
            <Link to="/passwort-vergessen">Neuen Link anfordern</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password" className="mb-2 block">Neues Passwort</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                required
                minLength={6}
                className="pr-12"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 hover:text-ink"
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="mb-2 block">Passwort bestätigen</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              required
              data-testid="reset-password-confirm"
            />
          </div>

          {error && (
            <p className="text-red-700 font-medium" data-testid="reset-password-error">{error}</p>
          )}

          <Button type="submit" disabled={loading} className="btn--block" data-testid="reset-password-submit">
            {loading ? 'Wird gespeichert...' : (<><Key className="w-5 h-5" />Passwort ändern</>)}
          </Button>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPasswordPage;
